import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  fetchCampaigns,
  executeScript,
  getCampaignStatus,
  Campaign,
  ScriptOutput,
  continuePreviousCallingList
} from 'services/campaignuploading.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhoneVolume,
  faFolder,
  faAngleRight,
  faXmark,
  faWindowMinimize,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';

interface CampaignConsole {
  id: number;
  name: string;
  outputs: ScriptOutput[];
  isMinimized: boolean;
  startTime: Date;
  isFinished: boolean;
  endTime?: Date;
}

const CampaignCallListUploading: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [consoles, setConsoles] = useState<Map<number, CampaignConsole>>(
    new Map()
  );
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archiveUploads, setArchiveUploads] = useState<{
    [key: number]: boolean;
  }>({});

  const consoleEndRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Efecto para hacer scroll al último mensaje
  useEffect(() => {
    Object.values(consoleEndRefs.current).forEach(ref => {
      if (ref && ref.parentElement) {
        // En vez de scrollIntoView, usamos el scrollTop del contenedor padre
        ref.parentElement.scrollTop = ref.parentElement.scrollHeight;
      }
    });
  }, [consoles]);

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Polling para verificar estado de campañas
  useEffect(() => {
    const interval = setInterval(async () => {
      const campaignsToCheck = campaigns.filter(
        c =>
          // Solo verificar campañas que tengan una consola activa
          consoles.has(c.id) && c.isRunning
      );

      if (campaignsToCheck.length === 0) {
        return; // No hay nada que verificar
      }

      const updatedCampaigns = [...campaigns];
      let needsUpdate = false;

      for (const campaign of campaignsToCheck) {
        try {
          const isStillRunning = await getCampaignStatus(campaign.id);
          const campaignIndex = updatedCampaigns.findIndex(
            c => c.id === campaign.id
          );

          if (
            campaignIndex !== -1 &&
            updatedCampaigns[campaignIndex].isRunning !== isStillRunning
          ) {
            updatedCampaigns[campaignIndex] = {
              ...updatedCampaigns[campaignIndex],
              isRunning: isStillRunning
            };
            needsUpdate = true;

            // Si la campaña terminó, actualizar la consola
            if (!isStillRunning) {
              setConsoles(prev => {
                const newConsoles = new Map(prev);
                const console = newConsoles.get(campaign.id);
                if (console) {
                  console.outputs = [
                    ...console.outputs,
                    {
                      type: 'exit',
                      content: 'Script execution completed',
                      timestamp: new Date().toISOString(),
                      campaign: campaign.name,
                      campaignId: campaign.id
                    }
                  ];
                }
                return newConsoles;
              });
            }
          }
        } catch (error) {
          console.error('Error checking campaign status:', error);
        }
      }

      if (needsUpdate) {
        setCampaigns(updatedCampaigns);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [campaigns, consoles]);

  const loadCampaigns = async () => {
    try {
      const data = await fetchCampaigns();
      setCampaigns(data);

      // Inicializar consolas para campañas que están en ejecución
      const runningCampaigns = data.filter(campaign => campaign.isRunning);
      if (runningCampaigns.length > 0) {
        setConsoles(prev => {
          const newConsoles = new Map(prev);
          runningCampaigns.forEach(campaign => {
            // Solo crear consola si no existe ya
            if (!newConsoles.has(campaign.id)) {
              newConsoles.set(campaign.id, {
                id: campaign.id,
                name: campaign.name,
                outputs: [],
                isMinimized: false,
                startTime: new Date(),
                isFinished: false,
                endTime: undefined
              });
            }
          });
          return newConsoles;
        });
      }

      // Inicializar el estado de los checkboxes de archivado para todas las campañas
      const initialArchiveState: { [key: number]: boolean } = {};
      data.forEach(campaign => {
        initialArchiveState[campaign.id] = true; // Seleccionado por defecto
      });
      setArchiveUploads(initialArchiveState);
    } catch (error) {
      setError('Error loading campaigns');
      console.error('Error loading campaigns:', error);
    }
  };

  const handleExecuteScript = useCallback(
    (campaign: Campaign) => {
      // Primero verificar si ya hay una consola para esta campaña
      if (consoles.has(campaign.id)) {
        setError(`Campaign ${campaign.name} is already running`);
        return () => {};
      }
      // Actualizamos el estado para marcar la campaña como en ejecución
      setCampaigns(prev =>
        prev.map(c => (c.id === campaign.id ? { ...c, isRunning: true } : c))
      );

      // Inicializar nueva consola
      setConsoles(prev => {
        const newConsoles = new Map(prev);
        newConsoles.set(campaign.id, {
          id: campaign.id,
          name: campaign.name,
          outputs: [],
          isMinimized: false,
          startTime: new Date(),
          isFinished: false,
          endTime: undefined
        });
        return newConsoles;
      });

      // Obtener el valor del checkbox para esta campaña
      const shouldArchive = archiveUploads[campaign.id];

      // Ejecutamos el script
      const cleanup = executeScript(
        campaign.id,
        (newOutput: ScriptOutput) => {
          setConsoles(prev => {
            const newConsoles = new Map(prev);
            const current = newConsoles.get(campaign.id);
            if (current) {
              // Creamos un nuevo objeto para evitar mutaciones\n
              newConsoles.set(campaign.id, {
                ...current,
                outputs: [...current.outputs, newOutput]
              });
            }
            return newConsoles;
          });
        },
        (err: Error) => {
          setError(err.message);
          loadCampaigns(); // Recargar campañas\n
          // Marcar la campaña como finalizada en caso de error\n
          setCampaigns(prev =>
            prev.map(c =>
              c.id === campaign.id ? { ...c, isRunning: false } : c
            )
          );
        },
        () => {
          if (!campaign.isRunning) {
            setConsoles(prev => {
              const newConsoles = new Map(prev);
              const console = newConsoles.get(campaign.id);
              if (console) {
                console.outputs = [
                  ...console.outputs,
                  {
                    type: 'exit',
                    content: 'Script execution completed',
                    timestamp: new Date().toISOString(),
                    campaign: campaign.name,
                    campaignId: campaign.id
                  }
                ];
                console.isFinished = true;
                console.endTime = new Date();
              }
              return newConsoles;
            });
          }
          loadCampaigns(); // Recargar campañas cuando finalice\n
          setCampaigns(prev =>
            prev.map(c =>
              c.id === campaign.id ? { ...c, isRunning: false } : c
            )
          );
        },
        shouldArchive
      );

      return cleanup;
    },
    [consoles, archiveUploads]
  );

  const handleCloseConsole = (campaignId: number) => {
    setConsoles(prev => {
      const newConsoles = new Map(prev);
      newConsoles.delete(campaignId);
      return newConsoles;
    });
  };

  const handleToggleMinimize = (campaignId: number) => {
    setConsoles(prev => {
      const newConsoles = new Map(prev);
      const current = newConsoles.get(campaignId);
      if (current) {
        newConsoles.set(campaignId, {
          ...current,
          isMinimized: !current.isMinimized
        });
      }
      return newConsoles;
    });
  };

  const handleArchiveChange = (campaignId: number, checked: boolean) => {
    setArchiveUploads(prev => ({
      ...prev,
      [campaignId]: checked
    }));
  };

  const getElapsedTime = (startTime: Date, endTime?: Date) => {
    // Redondeamos a segundos directamente
    const elapsed =
      Math.round(
        (endTime ? endTime.getTime() : new Date().getTime()) -
          startTime.getTime()
      ) / 1000;

    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = Math.floor(elapsed % 60); // Aseguramos que sean enteros

    return [
      hours > 0 ? `${hours}h` : '',
      `${minutes}m`,
      `${seconds.toString().padStart(2, '0')}s`
    ]
      .join(' ')
      .trim();
  };

  // Renderizar las consolas activas
  const renderConsoles = () => {
    return Array.from(consoles.values()).map(console => (
      <Card key={console.id} className="mb-3">
        <Card.Header className="h5 mb-0 mt-0 pt-2 pb-2 bg-body-secondary">
          <div className="h5 d-flex justify-content-between align-items-end mb-0 pt-1">
            <div>HVA Server Console Output - {console.name}</div>
            <small className="ms-2 text-muted text-start">
              {
                // eslint-disable-next-line
                !console.isFinished ? getElapsedTime(console.startTime) : `Completed in ${getElapsedTime(console.startTime, console.endTime)}`
              }
            </small>
            <div className="p-0 mb-0">
              <Button
                variant="link"
                className="p-0 me-2 mb-0"
                onClick={() => handleToggleMinimize(console.id)}
              >
                <FontAwesomeIcon
                  icon={console.isMinimized ? faSquare : faWindowMinimize}
                  className="fs-10"
                />
              </Button>
              {!campaigns.find(c => c.id === console.id)?.isRunning && (
                <Button
                  variant="link"
                  className="p-0 mb-0"
                  onClick={() => handleCloseConsole(console.id)}
                >
                  <FontAwesomeIcon icon={faXmark} className="fs-9" />
                </Button>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body className={console.isMinimized ? 'd-none' : ''}>
          <div
            id={`console-${console.id}`}
            className="bg-secondary text-light p-3 rounded fs-9 scrollbar"
            style={{
              height: '500px',
              overflowY: 'auto',
              fontFamily: 'monospace'
            }}
          >
            {console.outputs.map((entry, index) => (
              <div
                key={index}
                className={
                  entry.type === 'error'
                    ? 'text-danger-light'
                    : entry.type === 'exit'
                    ? 'text-success-light'
                    : 'text-light'
                }
              >
                <span className="text-info-light">
                  [{new Date(entry.timestamp).toLocaleTimeString()}]
                </span>{' '}
                {entry.content}
              </div>
            ))}
            <div
              ref={el => {
                if (el) {
                  consoleEndRefs.current[console.id] = el;
                }
              }}
            />
          </div>
        </Card.Body>
      </Card>
    ));
  };

  // Maneja la acción de continuar con el fichero anterior
  const handleContinuePrevious = async (campaign: Campaign) => {
    // Limpiar mensajes anteriores
    setError(null);
    setSuccessMessage(null);

    // Marcar como cargando
    setLoading(prev => ({ ...prev, [campaign.id]: true }));

    try {
      // Llamar al nuevo servicio
      const result = await continuePreviousCallingList(campaign.id);

      // Mostrar mensaje de éxito
      setSuccessMessage(result.message);

      // Registrar log de actividad en la consola
      console.log(`Continued with previous file for campaign ${campaign.name}`);

      // Recargar campañas para actualizar la interfaz
      await loadCampaigns();
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      // Desmarcar cargando
      setLoading(prev => ({ ...prev, [campaign.id]: false }));
    }
  };

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Call list Uploading</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Remote Call List uploading procedure execution
          </h5>
        </Col>
      </Row>
      <Row>
        {error && <div className="text-primary pb-3">{error}</div>}
        {successMessage && (
          <div className="text-success pb-3">{successMessage}</div>
        )}

        <Row>
          {/* Lista de Campañas con sus archivos */}
          <Col xl={12} xxl={4} className="mb-4">
            <Card>
              <Card.Header className="h5 mb-0 mt-0 pt-2 pb-2 bg-body-secondary">
                <div className="h5 d-flex justify-content-between align-items-end mb-0 pt-1">
                  Campaign Call List Awaiting Upload
                </div>
              </Card.Header>
              <Card.Body>
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="mb-0 fs-9 fw-bold">{campaign.name}</p>
                        <small className="text-muted">
                          Script: {campaign.scriptName}
                        </small>
                        <small className="text-muted d-block">
                          Available file:
                        </small>
                      </div>
                    </div>

                    {/* Files section */}
                    <div className="bg-body p-2 rounded mb-2">
                      {campaign.files.length > 0 ? (
                        campaign.files.map(file => (
                          <div
                            key={file.name}
                            className="d-flex justify-content-between align-items-center mb-0 ps-2 pe-2"
                          >
                            <small className="fw-bold text-primary">
                              <FontAwesomeIcon
                                icon={
                                  file.isDirectory ? faFolder : faPhoneVolume
                                }
                              />{' '}
                              {file.name}
                              {!file.isDirectory && ' ('}
                              {!file.isDirectory && file.modifiedDate}
                              {!file.isDirectory && ')'}
                            </small>
                            {!file.isDirectory && (
                              <small className="text-primary fw-bold">
                                {(file.size / 1024).toFixed(2)} Kb
                              </small>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="d-flex justify-content-between align-items-center mb-0 ps-2 pe-2">
                          <small className="text-muted">
                            No files available
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Verificar disponibilidad del script */}
                    {campaign.scriptAvailable && (
                      <>
                        {/* Checkbox para "Archivar cargas actuales" - solo visible cuando hay archivos */}
                        {campaign.files.length > 0 &&
                          campaign.files.some(file => !file.isDirectory) && (
                            <div className="mb-2 d-flex justify-content-end align-items-center">
                              <Form.Check
                                type="switch"
                                id={`archive-checkbox-${campaign.id}`}
                                label="Archive actual call list"
                                checked={archiveUploads[campaign.id] ?? true}
                                onChange={e =>
                                  handleArchiveChange(
                                    campaign.id,
                                    e.target.checked
                                  )
                                }
                                reverse
                              />
                            </div>
                          )}

                        {/* Botones de ejecución */}
                        <div className="d-flex flex-row-reverse gap-2">
                          {/* Botón para nueva carga - solo visible cuando hay archivos */}
                          {campaign.files.length > 0 &&
                            campaign.files.some(file => !file.isDirectory) && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleExecuteScript(campaign)}
                                disabled={campaign.isRunning}
                                endIcon={
                                  <FontAwesomeIcon
                                    icon={faAngleRight}
                                    className="mx-2 me-0"
                                  />
                                }
                                loading={campaign.isRunning}
                                loadingPosition="end"
                              >
                                {campaign.isRunning
                                  ? 'Executing... '
                                  : 'Execute new upload '}
                              </Button>
                            )}

                          {/* Botón para continuar con archivo anterior - siempre visible */}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleContinuePrevious(campaign)}
                            disabled={
                              campaign.isRunning || loading[campaign.id]
                            }
                            endIcon={
                              <FontAwesomeIcon
                                icon={faHistory}
                                className="mx-2 me-0"
                              />
                            }
                            loading={loading[campaign.id]}
                            loadingPosition="end"
                          >
                            {loading[campaign.id]
                              ? 'Processing... '
                              : 'Continue with actual call list'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>

          {/* Consola de Output */}
          <Col xl={12} xxl={8} className="mb-4">
            {renderConsoles()}
          </Col>
        </Row>
      </Row>
    </>
  );
};

export default CampaignCallListUploading;
